import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    IconButton,
    alpha,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    TextField,
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    PersonAdd as PersonAddIcon,
    SwapHoriz as SwapHorizIcon,
    Undo as UndoIcon,
} from '@mui/icons-material';
import { confirmMatches, MatchProfilesResponse, ConfirmMatchItem } from 'api/BaseballRecruits';
import { getIndividuals } from 'api/Group';
import { useSnackbar } from 'notistack';
import useTheme from '@mui/system/useTheme';
import { Group } from 'utils';

interface BaseballRecruitMatchReviewProps {
    open: boolean;
    onClose: () => void;
    results: MatchProfilesResponse;
    group: Group;
    onConfirmed: () => void;
}

function TabPanel({
    children,
    value,
    index,
}: {
    children?: React.ReactNode;
    value: number;
    index: number;
}) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const BaseballRecruitMatchReview: React.FC<BaseballRecruitMatchReviewProps> = ({
    open,
    onClose,
    results,
    group,
    onConfirmed,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const initialTab = results.matched?.length
        ? 0
        : results.individual_found?.length
        ? 1
        : results.unmatched?.length
        ? 2
        : 0;
    const [currentTab, setCurrentTab] = useState(initialTab);
    const [confirming, setConfirming] = useState(false);

    const [selectedMatched, setSelectedMatched] = useState<Set<number>>(new Set());
    const [matchedCreateOverrides, setMatchedCreateOverrides] = useState<Set<number>>(new Set());
    const [matchOverrides, setMatchOverrides] = useState<
        Record<number, { individual_uuid: string; member_name: string }>
    >({});

    const [individualSelections, setIndividualSelections] = useState<Record<number, string>>({});
    const [selectedIndividuals, setSelectedIndividuals] = useState<Set<number>>(new Set());
    const [individualCreateOverrides, setIndividualCreateOverrides] = useState<Set<number>>(
        new Set(),
    );

    const [unmatchedActions, setUnmatchedActions] = useState<
        Record<number, { type: 'link'; individual_uuid: string } | { type: 'create' }>
    >({});

    const [showSearchDialog, setShowSearchDialog] = useState(false);
    const [searchingFor, setSearchingFor] = useState<{
        category: 'matched' | 'individual' | 'unmatched';
        index: number;
        playerName: string;
    } | null>(null);
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [searching, setSearching] = useState(false);

    useEffect(() => {
        if (results?.matched) {
            const highConfidence = results.matched
                .map((m, i) => (m.confidence === 'high' ? i : null))
                .filter((i): i is number => i !== null);
            setSelectedMatched(new Set(highConfidence));
        }
        if (results?.individual_found) {
            const allIndices = results.individual_found.map((_, i) => i);
            setSelectedIndividuals(new Set(allIndices));
        }
    }, [results]);

    const searchIndividuals = async (name: string) => {
        try {
            setSearching(true);
            const orgUuid = (group as any).organization?.uuid;
            const params: Record<string, any> = { 'filter[name]': name, limit: 10 };
            if (orgUuid) params['filter[organization]'] = orgUuid;
            const response = await getIndividuals(params);
            setSearchResults(response.data.data || []);
        } catch {
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    const openSearchFor = (
        category: 'matched' | 'individual' | 'unmatched',
        index: number,
        playerName: string,
    ) => {
        setSearchingFor({ category, index, playerName });
        setShowSearchDialog(true);
        searchIndividuals(playerName);
    };

    const handleSelectIndividual = (individualUuid: string, individualName: string) => {
        if (!searchingFor) return;
        const { category, index } = searchingFor;
        if (category === 'matched') {
            setMatchOverrides((prev) => ({
                ...prev,
                [index]: { individual_uuid: individualUuid, member_name: individualName },
            }));
            setSelectedMatched((prev) => new Set(prev).add(index));
        } else if (category === 'individual') {
            setIndividualSelections((prev) => ({ ...prev, [index]: individualUuid }));
            setSelectedIndividuals((prev) => new Set(prev).add(index));
        } else {
            setUnmatchedActions((prev) => ({
                ...prev,
                [index]: { type: 'link', individual_uuid: individualUuid },
            }));
        }
        setShowSearchDialog(false);
        setSearchingFor(null);
    };

    const handleCreateNew = (index: number) => {
        setUnmatchedActions((prev) => ({
            ...prev,
            [index]: { type: 'create' },
        }));
    };

    const handleCreateNewForMatched = (index: number) => {
        setMatchedCreateOverrides((prev) => new Set(prev).add(index));
        setSelectedMatched((prev) => new Set(prev).add(index));
    };

    const handleConfirm = async () => {
        setConfirming(true);
        try {
            const matchItems: ConfirmMatchItem[] = [];

            results.matched.forEach((m, i) => {
                if (!selectedMatched.has(i)) return;
                if (matchedCreateOverrides.has(i)) {
                    matchItems.push({
                        profile_uuid: m.profile_uuid,
                        create_individual: {
                            first_name: m.first_name || m.player_name.split(' ')[0] || '',
                            last_name:
                                m.last_name || m.player_name.split(' ').slice(1).join(' ') || '',
                        },
                    });
                } else {
                    const override = matchOverrides[i];
                    const individualUuid = override?.individual_uuid || m.individual_uuid;
                    if (individualUuid) {
                        matchItems.push({
                            profile_uuid: m.profile_uuid,
                            individual_uuid: individualUuid,
                        });
                    }
                }
            });

            results.individual_found.forEach((m, i) => {
                if (!selectedIndividuals.has(i)) return;
                if (individualCreateOverrides.has(i)) {
                    matchItems.push({
                        profile_uuid: m.profile_uuid,
                        create_individual: {
                            first_name: m.first_name || m.player_name.split(' ')[0] || '',
                            last_name:
                                m.last_name || m.player_name.split(' ').slice(1).join(' ') || '',
                        },
                    });
                } else {
                    const overriddenUuid = individualSelections[i];
                    if (overriddenUuid) {
                        matchItems.push({
                            profile_uuid: m.profile_uuid,
                            individual_uuid: overriddenUuid,
                        });
                    } else if (m.potential_individuals && m.potential_individuals.length > 0) {
                        matchItems.push({
                            profile_uuid: m.profile_uuid,
                            individual_uuid: m.potential_individuals[0].individual_uuid,
                        });
                    }
                }
            });

            results.unmatched.forEach((m, i) => {
                const action = unmatchedActions[i];
                if (!action) return;
                if (action.type === 'link') {
                    matchItems.push({
                        profile_uuid: m.profile_uuid,
                        individual_uuid: action.individual_uuid,
                    });
                } else {
                    matchItems.push({
                        profile_uuid: m.profile_uuid,
                        create_individual: {
                            first_name: m.first_name || m.player_name.split(' ')[0] || '',
                            last_name:
                                m.last_name || m.player_name.split(' ').slice(1).join(' ') || '',
                        },
                    });
                }
            });

            if (matchItems.length === 0) {
                enqueueSnackbar('No matches to confirm', { variant: 'warning' });
                setConfirming(false);
                return;
            }

            const response = await confirmMatches(group.uuid, matchItems);
            enqueueSnackbar(response.data.message, { variant: 'success' });
            onConfirmed();
            onClose();
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to confirm matches', {
                variant: 'error',
            });
        } finally {
            setConfirming(false);
        }
    };

    const matchedCount = results.matched?.length || 0;
    const individualFoundCount = results.individual_found?.length || 0;
    const unmatchedCount = results.unmatched?.length || 0;
    const selectedCount = selectedMatched.size;

    const selectedIndividualCount = selectedIndividuals.size;
    const resolvedUnmatchedCount = Object.keys(unmatchedActions).length;

    const totalToConfirm = selectedCount + selectedIndividualCount + resolvedUnmatchedCount;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Review Player Matches</Typography>
                    <IconButton onClick={onClose} disabled={confirming}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                        Review how scraped players were matched to your group members and
                        organization individuals. Confirm to link profiles.
                    </Typography>
                    <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                        <Chip
                            icon={<CheckCircleIcon />}
                            label={`${selectedCount}/${matchedCount} Auto-matched`}
                            color="success"
                            size="small"
                        />
                        {individualFoundCount > 0 && (
                            <Chip
                                icon={<WarningIcon />}
                                label={`${individualFoundCount} Possible Matches`}
                                color="warning"
                                size="small"
                            />
                        )}
                        {unmatchedCount > 0 && (
                            <Chip
                                icon={<ErrorIcon />}
                                label={`${unmatchedCount} Unmatched`}
                                color="error"
                                size="small"
                            />
                        )}
                    </Box>
                </Alert>

                <Box
                    sx={{
                        borderBottom: 2,
                        borderColor: 'divider',
                        bgcolor: alpha('#f5f5f5', 0.6),
                        borderRadius: '8px 8px 0 0',
                        px: 1,
                        pt: 0.5,
                    }}
                >
                    <Tabs
                        value={currentTab}
                        onChange={(_, v) => setCurrentTab(v)}
                        sx={{
                            '& .MuiTab-root': {
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                textTransform: 'none',
                                minHeight: 48,
                                borderRadius: '8px 8px 0 0',
                                mx: 0.5,
                            },
                            '& .Mui-selected': {
                                bgcolor: 'background.paper',
                                boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
                            },
                            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
                        }}
                    >
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                                    {`Auto-Matched (${selectedCount}/${matchedCount})`}
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                                    {`Possible Matches (${selectedIndividualCount}/${individualFoundCount})`}
                                </Box>
                            }
                            disabled={individualFoundCount === 0}
                        />
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />
                                    {`Unmatched (${unmatchedCount})`}
                                    {unmatchedCount > 0 && (
                                        <Chip
                                            label={unmatchedCount}
                                            size="small"
                                            color="error"
                                            sx={{
                                                height: 22,
                                                fontWeight: 800,
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    )}
                                </Box>
                            }
                            disabled={unmatchedCount === 0}
                        />
                    </Tabs>
                </Box>

                {/* Auto-Matched Tab */}
                <TabPanel value={currentTab} index={0}>
                    {matchedCount > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                    setSelectedMatched(new Set(results.matched.map((_, i) => i)))
                                }
                            >
                                Select All
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setSelectedMatched(new Set())}
                            >
                                Deselect All
                            </Button>
                        </Box>
                    )}
                    {matchedCount === 0 ? (
                        <Alert severity="info">
                            No profiles were auto-matched to existing group members.
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={
                                                    selectedCount === matchedCount &&
                                                    matchedCount > 0
                                                }
                                                indeterminate={
                                                    selectedCount > 0 &&
                                                    selectedCount < matchedCount
                                                }
                                                onChange={(e) =>
                                                    e.target.checked
                                                        ? setSelectedMatched(
                                                              new Set(
                                                                  results.matched.map((_, i) => i),
                                                              ),
                                                          )
                                                        : setSelectedMatched(new Set())
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>Scraped Player</TableCell>
                                        <TableCell>Matched Member</TableCell>
                                        <TableCell>Confidence</TableCell>
                                        <TableCell>School</TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                Actions
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<PersonAddIcon />}
                                                    sx={{
                                                        ml: 1,
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        py: 0.25,
                                                        px: 1,
                                                    }}
                                                    onClick={() => {
                                                        const all = new Set(
                                                            results.matched.map((_, i) => i),
                                                        );
                                                        setMatchedCreateOverrides(all);
                                                        setSelectedMatched(all);
                                                    }}
                                                >
                                                    Create All
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.matched.map((match, index) => {
                                        const override = matchOverrides[index];
                                        return (
                                            <TableRow
                                                key={match.profile_uuid}
                                                sx={
                                                    override
                                                        ? {
                                                              bgcolor: alpha(
                                                                  theme.palette.info.main,
                                                                  0.05,
                                                              ),
                                                          }
                                                        : {}
                                                }
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedMatched.has(index)}
                                                        onChange={() => {
                                                            setSelectedMatched((prev) => {
                                                                const next = new Set(prev);
                                                                next.has(index)
                                                                    ? next.delete(index)
                                                                    : next.add(index);
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {match.player_name}
                                                    </Typography>
                                                    {match.class_year && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                        >
                                                            Class of {match.class_year}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {override ? (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={0.5}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    textDecoration: 'line-through',
                                                                    color: 'text.disabled',
                                                                }}
                                                            >
                                                                {match.member_name}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={600}
                                                                color="info.main"
                                                            >
                                                                {override.member_name}
                                                            </Typography>
                                                            <Chip
                                                                label="Reassigned"
                                                                size="small"
                                                                color="info"
                                                            />
                                                            <Tooltip title="Undo reassignment">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setMatchOverrides(
                                                                            (prev) => {
                                                                                const next = {
                                                                                    ...prev,
                                                                                };
                                                                                delete next[index];
                                                                                return next;
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    <UndoIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2">
                                                            {match.member_name}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={match.confidence}
                                                        size="small"
                                                        color={
                                                            match.confidence === 'high'
                                                                ? 'success'
                                                                : match.confidence === 'medium'
                                                                ? 'warning'
                                                                : 'default'
                                                        }
                                                    />
                                                    {match.score && (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ ml: 0.5 }}
                                                        >
                                                            ({(match.score * 100).toFixed(0)}%)
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {match.school || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {matchedCreateOverrides.has(index) ? (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1}
                                                        >
                                                            <Chip
                                                                icon={<PersonAddIcon />}
                                                                label="Will Create"
                                                                color="success"
                                                                size="small"
                                                            />
                                                            <Tooltip title="Undo — use original match">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setMatchedCreateOverrides(
                                                                            (prev) => {
                                                                                const next =
                                                                                    new Set(prev);
                                                                                next.delete(index);
                                                                                return next;
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    <UndoIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    ) : (
                                                        <Box display="flex" gap={0.5}>
                                                            <Tooltip title="Reassign to different individual">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        openSearchFor(
                                                                            'matched',
                                                                            index,
                                                                            match.player_name,
                                                                        )
                                                                    }
                                                                >
                                                                    <SwapHorizIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Not a match — create new individual">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() =>
                                                                        handleCreateNewForMatched(
                                                                            index,
                                                                        )
                                                                    }
                                                                >
                                                                    <PersonAddIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                {/* Possible Matches Tab (Individuals found in org but not group members) */}
                <TabPanel value={currentTab} index={1}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        These players matched individuals in your organization who are not yet
                        members of this group. Confirming will create a group membership and link
                        the profile.
                    </Alert>
                    {individualFoundCount === 0 ? (
                        <Alert severity="info">No possible matches found.</Alert>
                    ) : (
                        <>
                            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                        setSelectedIndividuals(
                                            new Set(results.individual_found.map((_, i) => i)),
                                        )
                                    }
                                >
                                    Select All
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setSelectedIndividuals(new Set())}
                                >
                                    Deselect All
                                </Button>
                            </Box>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={
                                                        selectedIndividualCount ===
                                                            individualFoundCount &&
                                                        individualFoundCount > 0
                                                    }
                                                    indeterminate={
                                                        selectedIndividualCount > 0 &&
                                                        selectedIndividualCount <
                                                            individualFoundCount
                                                    }
                                                    onChange={(e) =>
                                                        e.target.checked
                                                            ? setSelectedIndividuals(
                                                                  new Set(
                                                                      results.individual_found.map(
                                                                          (_, i) => i,
                                                                      ),
                                                                  ),
                                                              )
                                                            : setSelectedIndividuals(new Set())
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>Scraped Player</TableCell>
                                            <TableCell>Possible Individual</TableCell>
                                            <TableCell>Confidence</TableCell>
                                            <TableCell>School</TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    Actions
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        startIcon={<PersonAddIcon />}
                                                        sx={{
                                                            ml: 1,
                                                            textTransform: 'none',
                                                            fontSize: '0.7rem',
                                                            py: 0.25,
                                                            px: 1,
                                                        }}
                                                        onClick={() => {
                                                            const all = new Set(
                                                                results.individual_found.map(
                                                                    (_, i) => i,
                                                                ),
                                                            );
                                                            setIndividualCreateOverrides(all);
                                                            setSelectedIndividuals(all);
                                                        }}
                                                    >
                                                        Create All
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {results.individual_found.map((item, index) => {
                                            const best = item.potential_individuals?.[0];
                                            const overridden = individualSelections[index];
                                            return (
                                                <TableRow key={item.profile_uuid}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={selectedIndividuals.has(index)}
                                                            onChange={() => {
                                                                setSelectedIndividuals((prev) => {
                                                                    const next = new Set(prev);
                                                                    next.has(index)
                                                                        ? next.delete(index)
                                                                        : next.add(index);
                                                                    return next;
                                                                });
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight={600}
                                                        >
                                                            {item.player_name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {overridden ? (
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={0.5}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                    color="info.main"
                                                                >
                                                                    Reassigned
                                                                </Typography>
                                                                <Tooltip title="Undo">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setIndividualSelections(
                                                                                (prev) => {
                                                                                    const next = {
                                                                                        ...prev,
                                                                                    };
                                                                                    delete next[
                                                                                        index
                                                                                    ];
                                                                                    return next;
                                                                                },
                                                                            );
                                                                        }}
                                                                    >
                                                                        <UndoIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        ) : best ? (
                                                            <Typography variant="body2">
                                                                {best.individual_name}
                                                            </Typography>
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {best && !overridden && (
                                                            <>
                                                                <Chip
                                                                    label={best.confidence}
                                                                    size="small"
                                                                    color={
                                                                        best.confidence === 'high'
                                                                            ? 'success'
                                                                            : best.confidence ===
                                                                              'medium'
                                                                            ? 'warning'
                                                                            : 'default'
                                                                    }
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ ml: 0.5 }}
                                                                >
                                                                    ({(best.score * 100).toFixed(0)}
                                                                    %)
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {item.school || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {individualCreateOverrides.has(index) ? (
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1}
                                                            >
                                                                <Chip
                                                                    icon={<PersonAddIcon />}
                                                                    label="Will Create"
                                                                    color="success"
                                                                    size="small"
                                                                />
                                                                <Tooltip title="Undo — use suggested match">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setIndividualCreateOverrides(
                                                                                (prev) => {
                                                                                    const next =
                                                                                        new Set(
                                                                                            prev,
                                                                                        );
                                                                                    next.delete(
                                                                                        index,
                                                                                    );
                                                                                    return next;
                                                                                },
                                                                            );
                                                                        }}
                                                                    >
                                                                        <UndoIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        ) : (
                                                            <Box display="flex" gap={0.5}>
                                                                <Tooltip title="Search for different individual">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            openSearchFor(
                                                                                'individual',
                                                                                index,
                                                                                item.player_name,
                                                                            )
                                                                        }
                                                                    >
                                                                        <SwapHorizIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Not a match — create new individual">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="primary"
                                                                        onClick={() => {
                                                                            setIndividualCreateOverrides(
                                                                                (prev) =>
                                                                                    new Set(
                                                                                        prev,
                                                                                    ).add(index),
                                                                            );
                                                                            setSelectedIndividuals(
                                                                                (prev) =>
                                                                                    new Set(
                                                                                        prev,
                                                                                    ).add(index),
                                                                            );
                                                                        }}
                                                                    >
                                                                        <PersonAddIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </TabPanel>

                {/* Unmatched Tab */}
                <TabPanel value={currentTab} index={2}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        These players could not be matched to any member or individual. You can
                        search for an existing individual or create a new one.
                    </Alert>
                    {unmatchedCount === 0 ? (
                        <Alert severity="success">All players were matched.</Alert>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Scraped Player</TableCell>
                                        <TableCell>School</TableCell>
                                        <TableCell>Class / Positions</TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                Actions
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<PersonAddIcon />}
                                                    sx={{
                                                        ml: 1,
                                                        textTransform: 'none',
                                                        fontSize: '0.7rem',
                                                        py: 0.25,
                                                        px: 1,
                                                    }}
                                                    onClick={() => {
                                                        const allActions: typeof unmatchedActions =
                                                            {};
                                                        results.unmatched.forEach((_, i) => {
                                                            allActions[i] = { type: 'create' };
                                                        });
                                                        setUnmatchedActions(allActions);
                                                    }}
                                                >
                                                    Create All
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.unmatched.map((item, index) => {
                                        const action = unmatchedActions[index];
                                        return (
                                            <TableRow key={item.profile_uuid}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {item.player_name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{item.school || '—'}</TableCell>
                                                <TableCell>
                                                    {item.class_year &&
                                                        `Class of ${item.class_year}`}
                                                    {item.positions?.length > 0 &&
                                                        ` · ${item.positions.join(', ')}`}
                                                </TableCell>
                                                <TableCell>
                                                    {action ? (
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1}
                                                        >
                                                            <Chip
                                                                icon={<CheckCircleIcon />}
                                                                label={
                                                                    action.type === 'link'
                                                                        ? 'Linked'
                                                                        : 'Will Create'
                                                                }
                                                                color="success"
                                                                size="small"
                                                            />
                                                            <Tooltip title="Undo">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setUnmatchedActions(
                                                                            (prev) => {
                                                                                const next = {
                                                                                    ...prev,
                                                                                };
                                                                                delete next[index];
                                                                                return next;
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    <UndoIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    ) : (
                                                        <Box display="flex" gap={1}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<SwapHorizIcon />}
                                                                onClick={() =>
                                                                    openSearchFor(
                                                                        'unmatched',
                                                                        index,
                                                                        item.player_name,
                                                                    )
                                                                }
                                                            >
                                                                Search
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<PersonAddIcon />}
                                                                onClick={() =>
                                                                    handleCreateNew(index)
                                                                }
                                                            >
                                                                Create New
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={confirming} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={confirming || totalToConfirm === 0}
                    startIcon={confirming ? <CircularProgress size={18} /> : undefined}
                >
                    {confirming ? 'Confirming...' : `Confirm ${totalToConfirm} Match(es)`}
                </Button>
            </DialogActions>

            {/* Search Dialog */}
            <Dialog
                open={showSearchDialog}
                onClose={() => {
                    setShowSearchDialog(false);
                    setSearchingFor(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Search Individual
                    {searchingFor && (
                        <Typography variant="body2" color="text.secondary">
                            Player: <strong>{searchingFor.playerName}</strong>
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Type a name to search..."
                        defaultValue={searchingFor?.playerName || ''}
                        onChange={(e) => {
                            const val = e.target.value.trim();
                            if (val.length >= 2) searchIndividuals(val);
                        }}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    {searching ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress />
                        </Box>
                    ) : searchResults.length > 0 ? (
                        <List>
                            {searchResults.map((individual) => (
                                <ListItem
                                    key={individual.uuid}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        mb: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                                        },
                                    }}
                                    onClick={() =>
                                        handleSelectIndividual(individual.uuid, individual.name)
                                    }
                                >
                                    <ListItemText
                                        primary={individual.name}
                                        secondary={`In ${individual.groups?.length || 0} group(s)`}
                                    />
                                    <Button variant="outlined" size="small">
                                        Select
                                    </Button>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Alert severity="info">
                            No individuals found. Try a different search term.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowSearchDialog(false);
                            setSearchingFor(null);
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default BaseballRecruitMatchReview;
